/**
 * Contrato de sincronización sin conexión.
 *
 * Es el trozo de contrato más delicado del proyecto, porque gobierna qué pasa
 * cuando el teléfono lleva tres días sin señal y luego vuelve. Tres decisiones que
 * conviene tener presentes al leerlo:
 *
 *  · Cada operación de la cola lleva **su propia clave de idempotencia**. Es la
 *    única forma de que reintentar no duplique nada.
 *  · El cliente **genera el identificador** de lo que crea sin conexión. Así puede
 *    mostrar el movimiento en pantalla al instante, sin esperar al servidor.
 *  · El lote se responde **operación por operación**, para que el cliente borre de
 *    su cola solo lo que de verdad se aplicó.
 */
import { z } from 'zod';
import { zDateISO, zPositivePesos, zTimeHHmm, zUuid } from './common.js';
import { zTransactionCreate, zTransactionUpdate } from './entities.js';
import { zFiadoStoreCreate } from './entities.js';
/**
 * Operaciones que se pueden hacer sin conexión.
 *
 * Es una lista deliberadamente corta: solo lo que tiene sentido hacer desde el
 * móvil en la calle. No se puede, por ejemplo, cambiar la contraseña sin conexión,
 * ni hace falta.
 */
export const zSyncOperationType = z.enum([
    'transaction.create',
    'transaction.update',
    'transaction.delete',
    'fiadoStore.create',
    'goal.contribute',
    'recurring.run',
]);
export const zSyncOperation = z
    .object({
    /** Identificador de la operación en la cola del cliente. */
    id: zUuid,
    type: zSyncOperationType,
    /** Identificador de la entidad afectada. En las creaciones, lo pone el cliente. */
    entityId: zUuid,
    /** Clave de idempotencia: repetir la misma operación no la aplica dos veces. */
    idempotencyKey: z.string().trim().min(8).max(120),
    /** Cuándo se hizo, en el dispositivo. */
    queuedAt: z.string().trim().min(10).max(40),
    /** Datos de la operación. Se validan según el tipo al aplicarla. */
    payload: z.record(z.string(), z.unknown()).default({}),
})
    .strict();
export const zSyncPushRequest = z
    .object({
    /** Lote de operaciones, en el orden en que se hicieron. */
    operations: z.array(zSyncOperation).min(1).max(200),
})
    .strict();
export const zSyncOperationResult = z
    .object({
    id: zUuid,
    ok: z.boolean(),
    entityId: zUuid.optional(),
    /** Verdadero si la operación ya estaba aplicada (reintento). */
    duplicate: z.boolean().optional(),
    error: z.string().optional(),
})
    .strict();
export const zSyncPushResult = z
    .object({
    results: z.array(zSyncOperationResult),
    applied: z.number().int().min(0),
    failed: z.number().int().min(0),
    cursor: z.string(),
})
    .strict();
/**
 * Resultado de traer los cambios.
 *
 * Los tipos de cada colección se dejan como `unknown[]` a propósito: el cliente
 * los interpreta con sus propios esquemas de entidad, y duplicar aquí las 15
 * entidades haría que un cambio de modelo hubiera que tocarlo en tres sitios.
 * Lo que sí se fija es **la forma del sobre**: qué colecciones vienen y con qué
 * nombres.
 */
export const zSyncPullResult = z
    .object({
    /** Cursor para la próxima traída. */
    cursor: z.string(),
    /** Hora del servidor, para detectar relojes desajustados en el dispositivo. */
    serverTime: z.string(),
    /** Qué día es hoy en Colombia, según el servidor. */
    today: zDateISO,
    /** Si hay más de lo que cabía en esta página, conviene volver a pedir. */
    hasMore: z.boolean(),
    accounts: z.array(z.unknown()),
    categories: z.array(z.unknown()),
    transactions: z.array(z.unknown()),
    transactionItems: z.array(z.unknown()),
    transactionTags: z.array(z.unknown()),
    budgets: z.array(z.unknown()),
    debts: z.array(z.unknown()),
    debtPayments: z.array(z.unknown()),
    fiadoStores: z.array(z.unknown()),
    fiadoEntries: z.array(z.unknown()),
    recurringRules: z.array(z.unknown()),
    splits: z.array(z.unknown()),
    goals: z.array(z.unknown()),
    goalContributions: z.array(z.unknown()),
    tags: z.array(z.unknown()),
    settings: z.unknown().nullable(),
})
    .strict();
/** Lo que se envía al crear un movimiento desde la cola. */
export const zSyncTransactionCreate = zTransactionCreate;
export const zSyncTransactionUpdate = zTransactionUpdate;
export const zSyncFiadoStoreCreate = zFiadoStoreCreate;
/** Datos de un aporte a una meta hecho sin conexión. */
export const zSyncGoalContribute = z
    .object({
    goalId: zUuid,
    amount: zPositivePesos,
    date: zDateISO,
    time: zTimeHHmm.default('12:00'),
})
    .strict();
//# sourceMappingURL=sync.js.map